/**
 * Response ok-status checking -- the pure core of the HTTP check family, with `extractErrorText` in the same leaf
 * (checkResponseOk composes it, and the flat-leaf layers ban cross-leaf value imports). The BaseError-throwing assert
 * flavors live in the tools response-json leaf.
 *
 * HOMED IN `const/`, NOT `utils/` (board card #150-P2), and named in the `const/http-*` family spelling this flat
 * layer uses for `http-headers`, `http-auth-scheme` and `http-form-post-init`.
 * Its one value import is already `@federa/const`, and `utils/cloudflare/authenticated-json-call.ts` value-imports
 * {@link extractErrorText} -- a utils-to-utils value edge `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` forbids with no
 * allowlist. `const/` is the only home that consumer, the `tools/` cluster and the `flows/` and `components/`
 * callers all reach.
 *
 * NOTHING HERE IS IMPURE despite the `Response` argument: both bodies only READ an object the caller already holds.
 * The leaf opens no socket, binds no env and imports no runtime, so the const layer floor is intact.
 */
import { ERROR_MESSAGES } from '@federa/const/error-messages';

/** Safely read a response body as text, falling back on read failure.
 *  @runtime-derived-ssot-helper (utils-floor-pinned): classification arm (c) of
 *  `QUAL-CONST-FUNCTION-DISCIPLINE-001`'s fix -- `utils/cloudflare/authenticated-json-call.ts` value-imports this
 *  reader, and a `utils/` leaf may not value-import a utils sibling
 *  (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, HIGH, no allowlist), so relocation arm (b) is foreclosed. It also
 *  derives its miss arm from the same-layer `ERROR_MESSAGES.UNKNOWN_ERROR` SSOT. */
export async function extractErrorText(response: Response, defaultValue: string = ERROR_MESSAGES.UNKNOWN_ERROR): Promise<string> {
	try {
		return await response.text();
	} catch {
		return defaultValue;
	}
}

/** Check whether a response is ok (2xx status) without throwing.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes {@link extractErrorText}, its same-leaf sibling, and a
 *  `utils/` home is foreclosed for that sibling by the layer-floor constraint recorded above, so splitting this
 *  composition across two layers is not available. */
export async function checkResponseOk(
	response: Response,
	options: { skipBody?: boolean } = {},
): Promise<{ ok: true } | { ok: false; status: number; statusText: string; body?: string }> {
	if (response.ok) return { ok: true };
	const result: {
		ok: false;
		status: number;
		statusText: string;
		body?: string;
	} = {
		ok: false,
		status: response.status,
		statusText: response.statusText,
	};
	if (!options.skipBody) result.body = await extractErrorText(response);
	return result;
}
